import { ActionIcon, Icon, useShowNotification } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { RealNameModal } from '@inflearn/modals';
import { faArrowDownToLine } from '@inflearn/pro-solid-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { getAccountProfileQueryKey } from '../../../../queries/useClientAccountProfileQuery';
import useMediaQuery from '../../../../styles/useMediaQuery';
import useVoucherEnrollmentVerificationConfirmUpdateMutation from './mutations/useVoucherEnrollmentVerificationConfirmUpdateMutation';
import {
  CERTIFICATE_WIDTH,
  CERTIFICATE_WRAPPER_ID,
  type CertificationOfVoucherTemplateProps,
  makeCertificationOfVoucherTemplate,
} from './template/CertificationOfVoucherTemplate';

type DownloadCertificationButtonProps = {
  realName?: string;
  voucher: {
    id: number;
    courseId: number;
    createdAt: string;
    courseTitle: string;
  };
};

export default function DownloadCertificationButton({
  realName,
  voucher,
}: DownloadCertificationButtonProps) {
  const { t } = useTranslation();
  const isVerifiedRealName = !!realName;
  const { lang } = useLang();

  const { isMobile } = useMediaQuery();
  const { showNotification } = useShowNotification();
  const { mutate } = useVoucherEnrollmentVerificationConfirmUpdateMutation();

  const [isOpened, handler] = useDisclosure(false);
  const [isLoadingForCreatePDF, setLoading] = useState(false);

  const handleDownloadCertificationOfVoucher = async () => {
    if (!isVerifiedRealName) {
      handler.open();

      return;
    }

    try {
      setLoading(true);
      const canvas = await makeCanvasForPDF({
        templateData: {
          voucher,
          realName,
        },
      });
      await downloadCertificationPDF(canvas);
      setLoading(false);
      mutate({ voucherId: voucher.id });
    } catch (e) {
      console.log(e);
      setLoading(false);
      showNotification({
        type: 'error',
        title: t('수강 확인증 다운로드에 문제가 발생했습니다.'),
      });
    }
  };

  return (
    <>
      <ActionIcon
        radius="sm"
        variant="subtle"
        aria-label={t('수강확인증 다운로드')}
        size={isMobile ? 'n-xs' : 'n-sm'}
        loading={isLoadingForCreatePDF}
        onClick={handleDownloadCertificationOfVoucher}
      >
        <Icon icon={faArrowDownToLine} size="sm" aria-hidden />
      </ActionIcon>
      {isOpened && (
        <RealNameModal
          lang={lang}
          type="enrollment"
          opened={isOpened}
          onClose={handler.close}
          invalidateQueryKey={getAccountProfileQueryKey()}
        />
      )}
    </>
  );
}

type MakeImageUrlParams = {
  templateData: CertificationOfVoucherTemplateProps;
};

const makeCanvasForPDF = async ({ templateData }: MakeImageUrlParams) => {
  if (isNil(templateData)) {
    throw new Error('makePdfImageUrl: templateData is required to generate pdf image url');
  }

  const pdfTemplate = makeCertificationOfVoucherTemplate({
    voucher: templateData.voucher,
    realName: templateData.realName,
  });

  const { htmlToCanvas } = await import('@inflearn/pdf');

  return await htmlToCanvas({
    html: pdfTemplate,
    canvasWrapperId: CERTIFICATE_WRAPPER_ID,
  });
};

const downloadCertificationPDF = async (canvas: HTMLCanvasElement) => {
  const { downloadPDF, canvasToImageUrl } = await import('@inflearn/pdf');
  const imageUrl = await canvasToImageUrl(canvas);

  void downloadPDF({
    fileName: 'course_enrollment',
    image: {
      url: imageUrl,
      width: CERTIFICATE_WIDTH,
      height: (canvas.height * CERTIFICATE_WIDTH) / canvas.width,
    },
    orientation: 'portrait',
  });
};
